import { useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useApiStore } from '../store/useApiStore'

/**
 * Wraps the ApiClient with Auth0 token injection.
 *
 * @returns {ReturnType<typeof useApiStore>['api']} A proxied API client
 */
export function useApi() {
  const authDisabled = import.meta.env.VITE_APP_DISABLE_AUTH0 === 'true'
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0()
  const api = useApiStore((state) => state.api)

  const apiWithAuth = useMemo(() => {
    /**
     * Wrap an api method so it injects Authorization + X-ID-Token headers.
     *
     * @template {(...args: any[]) => any} T
     * @param {T} fn
     * @returns {(...args: Parameters<T>) => ReturnType<T>}
     */
    const withToken =
      (fn) =>
      async (...args) => {
        const lastArgIsOpts =
          args.length > 1 &&
          typeof args.at(-1) === 'object' &&
          !Array.isArray(args.at(-1)) &&
          ('headers' in args.at(-1) ||
            'signal' in args.at(-1) ||
            'credentials' in args.at(-1))

        const opts = lastArgIsOpts ? args.pop() : {}
        opts.headers = { ...(opts.headers || {}) }

        const hasAuthHeader =
          typeof opts.headers.Authorization === 'string' ||
          typeof opts.headers.authorization === 'string'

        if (!hasAuthHeader) {
          const accessToken = authDisabled
            ? 'disabled'
            : await getAccessTokenSilently()
          opts.headers.Authorization = `Bearer ${accessToken}`
        }

        // Add ID token header unless caller already provided it
        if (typeof opts.headers['X-ID-Token'] !== 'string') {
          const idClaims = authDisabled ? 'disabled' : await getIdTokenClaims()
          opts.headers['X-ID-Token'] = idClaims?.__raw || idClaims
        }

        return fn(...args, opts)
      }

    return {
      get: withToken(api.get.bind(api)),
      post: withToken(api.post.bind(api)),
      put: withToken(api.put.bind(api)),
      delete: withToken(api.delete.bind(api)),

      streamAgentMessage: api.streamAgentMessage.bind(api),
      postMessage: withToken(api.postMessage.bind(api)),

      getAllConversations: withToken(api.getAllConversations.bind(api)),
      getConversationMessages: withToken(api.getConversationMessages.bind(api)),
      createConversationWithMessage: withToken(
        api.createConversationWithMessage.bind(api),
      ),
      updateConversation: withToken(api.updateConversation.bind(api)),
      deleteConversation: withToken(api.deleteConversation.bind(api)),
      updateMessage: withToken(api.updateMessage.bind(api)),

      getProjects: withToken(api.getProjects.bind(api)),
      archiveProject: withToken(api.archiveProject.bind(api)),
      unarchiveProject: withToken(api.unarchiveProject.bind(api)),
    }
  }, [api, getAccessTokenSilently, getIdTokenClaims, authDisabled])

  const apiWithErrorToasts = useMemo(() => {
    return new Proxy(apiWithAuth, {
      get(target, prop) {
        const orig = target[prop]
        if (typeof orig !== 'function') return orig
        return async (...args) => {
          try {
            const res = await orig(...args)
            if (res && res.ok === false) {
              const err = new Error(
                res.body?.message || `HTTP ${res.status} Error`,
              )
              err.status = res.status
              err.response = res
              throw err
            }
            return res
          } catch (err) {
            if (err.name === 'AbortError') throw err
            throw err
          }
        }
      },
    })
  }, [apiWithAuth])

  return apiWithErrorToasts
}
